import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import CalendarImg from "../../../../../images/calendar.svg";
import Ical from "../../../../../images/ical.svg";
import IconButton from "../../../../iconButton";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ToggleButton from "../../../../commons/ToggleButton";
import Button from "../../../../button";
import {
  PlusCircleIcon,
  LinkIcon,
  DevicePhoneMobileIcon,
} from "@heroicons/react/24/outline";
import CustomInputField from "../../../../customInputField";
import { toast } from "react-toastify";
import DeleteRegionModal from "../../../../modal/DeleteRegionModal";
import {
  useGetUserDetailsQuery,
  useSaveAdminUserInfoMutation,
} from "../../../../../store/admin/professionalUserInfo";
import YouTubeEmbed from "../../../../videoPlayer";
import { PlayIcon } from "@heroicons/react/20/solid";

const ProfessionalWelcomeVideo = ({ isMobile }) => {
  const location = useLocation();
  const { professionalId } = location?.state ?? {};
  const [deleteIsLoading, setDeleteIsLoading] = useState(false);
  const [saveIsLoading, setSaveIsLoading] = useState(false);
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saveLink, setSaveLink] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [mode, setMode] = useState("add");
  const [isDelete, setIsDelete] = useState(false);
  const [action, setAction] = useState(null);

  const [
    saveProfessionalWelcomeVideoLink,
    { isLoading: videoLinkLoader, isSuccess: videoLinkSuccess },
  ] = useSaveAdminUserInfoMutation();

  const { data: professionalInfoDetail, refetch } =
    useGetUserDetailsQuery(professionalId);
  


  useEffect(() => {
    if (professionalInfoDetail && professionalInfoDetail?.data?.welcome_video) {
      setShowLinkInput(true);
      setMode("edit");
      setSaveLink(professionalInfoDetail?.data?.welcome_video);
    } else {
      setShowLinkInput(false);
      setMode("add");
    }
    setSaveIsLoading(false);
    setDeleteIsLoading(false);
  }, [professionalInfoDetail]);

  const handleShowInput = () => {
    setShowLinkInput(true);
  };
  useEffect(() => {
    if (videoLinkSuccess && !videoLinkLoader) {
      if (isDelete) {
        toast.success("Video Link deleted successfully.");
        handleClose();
        setIsDelete(false);
        refetch();
        return;
      }
      if (action == "add_link") {
        toast.success("Video Link added successfully.");
        setMode("edit");
      } else if (action == "edit_link") {
        toast.success("Video Link updated successfully.");
        setMode("edit");
      }
      setAction(null);
      setSaveIsLoading(false);
      setDeleteIsLoading(false);
      refetch();
    }
  }, [videoLinkSuccess]);


  const extractVideoId = (url) => {
    if (!url || url.length === 11) return false;
  
    const ytPatterns = [
      /youtu\.be\/([^?\/]+)/,
      /youtube\.com\/watch\?v=([^&]+)/,
      /youtube\.com\/embed\/([^?\/]+)/,
      /youtube\.com\/v\/([^?\/]+)/,
      /youtube\.com\/shorts\/([^?\/]+)/
    ];
  
    for (const pattern of ytPatterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
  
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return true;
  
    // Wistia
    const wistiaMatch = url.match(/wistia\.com\/medias\/([a-zA-Z0-9]+)/);
    if (wistiaMatch) return true;
  
    return false;
  };
  
  const handleAddingVideoLink = () => {
    if(!extractVideoId(saveLink))
    {
      console.log(saveLink)
      setErrorMessage("Unsupported video URL")
      return
    } 
    if (mode === "edit") {
      setAction("edit_link");
      setMode("add");
      return;
    } else {
      if (action != "edit_link") {
        setAction("add_link");
      }
      if (saveLink === "") {
        setErrorMessage("Video Link cannot be empty.");
        return;
      }
      setErrorMessage("");
      const params = {
        _method: "PUT",
        welcome_video: saveLink,
      };
      saveProfessionalWelcomeVideoLink({ professionalId, ...params });
      setSaveIsLoading(true);
    }
  };

  const handleDeletingVideoLink = () => {
    setDeleteIsLoading(true);
    setSaveLink("");
    const params = {
      _method: "PUT",
      welcome_video: "",
    };
    saveProfessionalWelcomeVideoLink({ professionalId, ...params });
  };

  const handleClose = () => {
    setShowDeleteModal(false);
  };
  

  return (
    <div className="">
      {!isMobile && (
        <h4 className="text-[22px] font-bold leading-[160%] text-customGray mb-6">
          Welcome Video
        </h4>
      )}
      <div className="border border-solid border-[#EBEFF3] py-4 px-5 rounded-md flex md:items-center justify-between flex-col md:flex-row gap-5 mb-6 bg-white">
        <div
          className={`flex flex-col w-full ${
            mode === "edit" ? "schedule-field" : "dark-field"
          }`}
        >
          <div className="mb-5">
            <h6 className="text-sm md:text-base font-bold leading-[160%] text-customGray mb-2 flex items-center gap-2 md:gap-5">
              <PlayIcon className="w-8 h-8 text-primary p-2" />
              Add a Welcome Video to Your Profile
            </h6>
            <p className="text-sm md:text-base font-normal leading-[160%] text-customGray mb-[5.5px]">
              Include a short introduction video on your profile to help your
              matches learn more about you and your practice. Your matches will
              have the option to play this video (it will not auto-play).
            </p>
          </div>
            <CustomInputField
              icon={LinkIcon}
              placeHolder="Paste your video URL here (Youtube, Vimeo, or Wistia)..."
              type="text"
              value={saveLink}
              name="schedule_url"
              onChange={(e) => {
                setErrorMessage("");
                setSaveLink(e.target.value);
              }}
              disabled={mode === "edit"}
              classes={`${
                mode === "edit" ? "bg-[#F1F4F6]" : "bg-[#FFFFFF]"
              } flex items-center mx-auto w-full border border-solid border-[#CCD0D4] rounded-[10px] h-[48px] overflow-hidden mb-5`}
              inputClasses={`border-r-0 text-sm font-medium text-customGray placeholder:text-[#869099] pl-[10px] pr-[20px] ${
                mode === "edit" ? "bg-[#F1F4F6]" : "bg-transparent"
              }`}
            />
            {professionalInfoDetail?.data?.welcome_video && saveLink&&
            <div className="relative w-full h-full aspect-video mb-5 mx-auto">
            <YouTubeEmbed 
              url={saveLink}
              width="100%"
              height="100%"
            />
          </div>
              }
          {errorMessage && (
            <p className="text-red-500 font-semibold text-sm mb-2 -mt-2">
              {errorMessage}
            </p>
          )}
          {!showLinkInput && (
            <div>
              <IconButton
                title="add new"
                classes="text-sm font-black leading-[160%] uppercase text-primary flex items-center justify-center rounded-[10px] h-10 mb-2 uppercase border border-solid border-primary hover:bg-[#EBF7FF] capitalize py-[9px] px-[19px]"
                iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
                icon={PlusCircleIcon}
                onClick={handleShowInput}
              />
            </div>
          )}
          <div className="grid grid-cols-2 gap-4 justify-start max-w-[300px] w-full">
            {showLinkInput && (
              <Button
                title={mode === "add" ? "Save" : "Edit"}
                type="button"
                classes="text-sm font-black leading-[160%] uppercase text-primary flex items-center justify-center rounded-[10px] h-10 uppercase border border-solid border-primary hover:bg-[#EBF7FF] capitalize py-[9px] px-[35px]"
                onClick={handleAddingVideoLink}
                buttonLoader={saveIsLoading}
              />
            )}
            {professionalInfoDetail?.data?.welcome_video && (
              <IconButton
                icon={null}
                iconClasses="w-6 h-6 text-[#EF2121]"
                title="Delete"
                classes="text-sm font-black leading-[160%] text-[#EF2121] w-full flex items-center justify-center gap-1.5 border border-solid border-[#EF2121] rounded-[10px] h-10 w-full"
                onClick={() => {
                  setShowDeleteModal(true);
                  setIsDelete(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      <DeleteRegionModal
        isOpen={showDeleteModal}
        handleClose={handleClose}
        handleDelete={handleDeletingVideoLink}
        text="Do you really want to delete this Welcome Video link?"
        loader={deleteIsLoading}
      />
    </div>
  );
};

export default ProfessionalWelcomeVideo;
